import { AppThunk, RootState } from "../store";
import { loadSideMenuFromEvent } from "../schedule/scheduleActions";
import { createAsyncThunk, ThunkAction, ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import { WinState } from "./winSlice";
import { actionReducerItems, dateInReloadRange } from "../common/commonActions";
import { RdsState } from "../rds/rdsSlice";
import { rdsEvent_Dto } from "../../interfaces/dtos/rdsEvent";
import { loadRdsSelections } from "../rds/rdsActionsSelection";
import { loadRdsRunner } from "../rds/rdsActionsRunner";
import { apiGet, apiPost } from "../../utils/api";
import { apiUrls } from "../../components/shared/apiUrls";
import { Win } from "../../interfaces/dtos/win";
import { getWinCalculated } from "./winCalculated";
import { loadRdsEventStatuses } from "../rds/rdsActionsEventStatus";
import { compareDateStrings, toUKDate } from "../../utils/commonUtils";

export const loadEventWin = (eventId: number, forReloading?: boolean): AppThunk =>
    async (dispatch, getState) => {

        const reloading = forReloading ?? false;

        await actionScheduleForItem(dispatch, getState().win, reloading, async () => {

            await dispatch(loadSideMenuFromEvent(eventId));
            const event = getState().rds.rdsEvents.find((e) => e.id === eventId);

            if (event && !mustReload(event, getState().rds, getState().win, reloading)) {
                return;
            }

            dispatch(loadRdsEventStatuses(eventId));  // Don't need await as can load syncronously
            await dispatch(loadRdsSelections(eventId));  // wait for selections to load as used below
            const selections = getState().rds.rdsSelections.filter((s) => s.eventId === eventId);
            const runnerFuncs = selections.map((s) => dispatch(loadRdsRunner({
                selectionId: s.id,
                selectionStatus: s.status
            })));
            const loadEventWinFunc = dispatch(loadEventWinItem(eventId));
            await Promise.allSettled([...runnerFuncs, loadEventWinFunc]);
        })
    };

const mustReload = (event: rdsEvent_Dto, rds: RdsState, win: WinState, forceReload: boolean) => {
    if (dateInReloadRange(event.dateTime)) {
        return true;
    }

    if (forceReload) {
        return true;
    }

    const selections = rds.rdsSelections.filter((s) => s.eventId === event.id);
    if (selections.length === 0) {
        return true;
    }

    const selectionIds = selections.map((s) => s.id);
    const runners = rds.rdsRunners.filter((r) => selectionIds.some((s) => s === r.id));
    if (runners.length === 0) {
        return true;
    }

    const eventWin = win.eventWins.filter((ew) => ew.eventId === event.id);
    if (eventWin.length === 0) {
        return true;
    }

    return false;
}

export const loadEventWinItem = createAsyncThunk(
    'win/loadEventWinItem',
    async (eventId: number, { getState }) => {

        const { win } = getState() as RootState;

        const result = await apiGet<Win[]>(apiUrls.winMarket.getAll(eventId));

        const otherWinEvents = win.eventWins.filter((ew) => ew.eventId !== eventId);
        const sortedWins = result.toSorted((a, b) => ((b.version - a.version || compareDateStrings(b.timestamp, a.timestamp))));

        return [...mapWins(sortedWins, eventId),...otherWinEvents ];
    }
);

const mapWins = (wins: Win[], eventId: number): Win[] => {
    return wins.map((w) => mapWin(w, eventId));
}

const mapWin = (win: Win, eventId: number): Win => {
    const timestamp = toUKDate(win.timestamp);
    return {
        ...win,
        eventId,
        timestamp,
        calculated: getWinCalculated(win)
    }
}

export const setLoading = createAsyncThunk(
    'win/setLoading',
    async (loading: boolean) => {
        return loading;
    }
);

export const setReloading = createAsyncThunk(
    'win/setReloading',
    async (loading: boolean) => {
        return loading;
    }
);

interface SuspendEPArgs {
    eventId: number;
    suspend: boolean;
    currentVersion: number;
}
export const suspendEP = createAsyncThunk(
    'win/suspendEP',
    async ({ eventId, suspend, currentVersion }: SuspendEPArgs, { dispatch, getState }) => {
        // Check that the current version we are using to suspend EP is still the most current version.
        await checkIsCurrentVersion(eventId, currentVersion, dispatch);
        const status = suspend ? "EP_SUSPEND" : "EP_UNSUSPEND";
        await apiPost(apiUrls.winMarket.updateSuspendEP(eventId, status), {});

        // Reload the win market
        const { win } = getState() as RootState;
        await actionScheduleForItem(dispatch, win, true, async () => {
            await dispatch(loadEventWinItem(eventId));
        })
        return undefined;
    }
);

interface UpdateBettingStateArgs {
    eventId: number;
    bettingState: string;
    currentVersion: number;
}
export const updateBettingState = createAsyncThunk(
    'win/updateBettingState',
    async ({ eventId, bettingState, currentVersion }: UpdateBettingStateArgs, { dispatch, getState }) => {
        // Check that the current version we are using to update betting state is still the most current version.
        await checkIsCurrentVersion(eventId, currentVersion, dispatch);
        await apiPost(apiUrls.winMarket.updateBettingState(eventId, bettingState.toUpperCase()), {});

        // Reload the win market
        const { win } = getState() as RootState;
        await actionScheduleForItem(dispatch, win, true, async () => {
            await dispatch(loadEventWinItem(eventId));
        })
        return undefined;
    }
);

interface IgnorePricesArgs {
    eventId: number;
    currentVersion: number;
}
export const ignorePrice = createAsyncThunk(
    'win/ignorePrice',
    async ({ eventId, currentVersion }: IgnorePricesArgs, { dispatch, getState }) => {
        // Check that the current version we are using to update betting state is still the most current version.
        await checkIsCurrentVersion(eventId, currentVersion, dispatch);
        await apiPost(apiUrls.winMarket.ignorePrices(eventId), {});

        // Reload the win market
        const { win } = getState() as RootState;
        await actionScheduleForItem(dispatch, win, true, async () => {
            await dispatch(loadEventWinItem(eventId));
        })
        return undefined;
    }
);

const checkIsCurrentVersion = async (
    eventId: number, currentVersion: number, dispatch: ThunkDispatch<unknown, unknown, UnknownAction>
) => {
    const result = await apiGet<Win[]>(apiUrls.winMarket.getAll(eventId));
    const maxVersion = Math.max(...result.map((r) => r.version));
    if (currentVersion !== maxVersion) {
        dispatch(loadEventWin(eventId, true) as ThunkAction<undefined, unknown, unknown, UnknownAction>);
        throw Error('This version is no longer the current version. Refreshing...');
    }
}

const actionScheduleForItem = async (
    // allow parameter to be any
    // eslint-disable-next-line
    dispatch: ThunkDispatch<any, any, any>,
    winState: WinState,
    reloading: boolean,
    process: () => Promise<void>
) => {
    await actionReducerItems(
        dispatch,
        reloading ? winState.reloading : winState.loading,
        reloading ? setReloading : setLoading,
        process
    );
}